<template>
	<div class="v-map">
		<l-map
			ref="map"
			:max-bounds="localMaxBounds"
			:zoom.sync="localZoom"
			:center.sync="localCenter"
			:bounds.sync="localBounds"
			class="v-map__wrapper"
		>
			<l-control-layers position="topright"/>
			<l-tile-layer
				v-for="tileProvider in tileProviders"
				:key="tileProvider.name"
				:name="tileProvider.name"
				:visible="tileProvider.visible"
				:url="tileProvider.url"
				:attribution="tileProvider.attribution"
				layer-type="base"
			/>
		
			<l-layer-group>
				<l-marker
					v-for="(latlag, index) in polylinePoints"
					:key="index"
					:lat-lng.sync="polylineLatlngs[index]"
					:draggable="true"
					@update:lat-lng="$emit('update:polyline-points', polylineLatlngs)"
					@contextmenu="menu.active"
				/>
				<l-polyline :lat-lngs="polylinePoints" color="#ccc" :weight="8"/>
				<l-polyline :lat-lngs="polylinePoints" color="#fff" :weight="6"/>
				<l-polyline :lat-lngs="polylinePoints" :color="polylineColor" :weight="2"/>
			</l-layer-group>
	
			<l-marker
				v-for="(point, index) in pointsLatlngs"
				:key="index"
				:lat-lng="point.latLng ? point.latLng : point"
				:draggable="point.draggable"
				@update:lat-lng="
					point.latLng
						? pointsLatlngs[index].latLng = $event
						: pointsLatlngs[index] = $event;
					$emit('update:points', pointsLatlngs);
				"
				@contextmenu="menu.active"
			>
				<l-icon
					v-if="!Array.isArray(point) && point.icon"
					:icon-size="point.icon.size || [30, 30]"
					:icon-anchor="point.icon.anchor || [18, 18]"
					:icon-url="point.icon.url"
				/>
				<l-popup v-if="point.popupText">{{point.popupText}}</l-popup>
			</l-marker>
	
			<l-control position="bottomleft">
				<slot name="bottom-left-area"/>
			</l-control>
			<l-control position="bottomright">
				<slot name="bottom-right-area"/>
			</l-control>
		</l-map>
	
		<!-- loader -->
		<v-progress-linear
			v-if="loading"
			indeterminate
			height="2"
			color="primary"
			class="mx-auto"
		/>
	
		<!-- menu -->
		<v-menu
			v-if="contextMenuItems.length"
			v-model="menu.show"
			open-delay="0"
			:position-x="menu.x"
			:position-y="menu.y"
			absolute
			offset-y
		>
			<v-list dense>
				<v-list-item
					v-for="(item, index) in contextMenuItems"
					:key="index"
					:class="item.class"
					@click="$emit(`context-menu-${item.action}`, menu.coordinates)"
				>
					<v-list-item-icon class="me-3">
						<v-icon :color="item.iconColor">{{item.icon}}</v-icon>
					</v-list-item-icon>
					<v-list-item-title>{{item.text}}</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script>
import L, { latLngBounds } from 'leaflet'
import { LMap, LTileLayer, LMarker, LControl, LControlLayers, LLayerGroup, LPolyline, LIcon, LPopup } from 'vue2-leaflet';
import 'leaflet/dist/leaflet.css';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
	iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
	iconUrl: require('leaflet/dist/images/marker-icon.png'),
	shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

export default {
	name: 'VMap',

	components: {
		LMap,
		LTileLayer,
		LMarker,
		LControl,
		LControlLayers,
		LLayerGroup,
		LPolyline,
		LIcon,
		LPopup,
	},

	props: {
		loading: { type: Boolean, default: false },
		disableMoving: { type: Boolean, default: false },
		points: { type: Array, default: () => ([]) },
		polylinePoints: { type: Array, default: () => ([]) },
		polylineColor: { type: String, default: '#42ab59' },
		// center: { type: [Object, Array], default: () => ({ lat: 24.45933025676469, lng: 54.37182232334757 }) },
		center: { type: [Object, Array], default: () => ({ lat: 29.375593343561235, lng: 47.97720909118653 }) },
		zoom: { type: Number, default: 15 },
		bounds: { type: Array, default: undefined },
		contextMenuItems: { type: Array, default: () => ([]) },
		maxBounds: { type: Object, default: undefined },
		maxMapBounds: {
			type: Object,
			default: () => (latLngBounds([
				[32.325653, 35.518799],
				[37.26362, 42.286377]
			]))
		},
	},
	
	data() {
		return {
			polylineLatlngs: [],
			pointsLatlngs: [],

			tileProviders: [
				{
					name: 'Google Maps',
					visible: true,
					url: 'https://mt1.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
				},
				{
					name: 'Google Satellite',
					visible: false,
					url: 'https://mt1.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
				},
				{
					name: 'Open street',
					visible: false,
					url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
				}
			],

			menu: {
				show: null,
				x: null,
				y: null,
				coordinates: null,
				active: (e) => {
					this.menu.x = e.originalEvent.clientX;
					this.menu.y = e.originalEvent.clientY;
					this.menu.coordinates = e.latlng;
					this.menu.show = true;
				}
			}
		};
	},

	watch: {
		polylinePoints: {
			handler: function (newVal) {
				this.polylineLatlngs = newVal;
			},
			immediate: true
		},
		points: {
			handler: function (newVal) {
				this.pointsLatlngs = newVal;
			},
			immediate: true
		},
		disableMoving: {
			handler: function (newVal) {
				const method = newVal ? 'disable' : 'enable';
				this.$refs.map.mapObject.dragging[method]();
				this.$refs.map.mapObject.touchZoom[method]();
				this.$refs.map.mapObject.doubleClickZoom[method]();
				this.$refs.map.mapObject.scrollWheelZoom[method]();
				this.$refs.map.mapObject.boxZoom[method]();
				this.$refs.map.mapObject.keyboard[method]();
				if (this.$refs.map.mapObject.tap) {
					this.$refs.map.mapObject.tap[method]();
				}
			}
		}
	},

	/* eslint-disable vue/order-in-components */
	computed: {
		localCenter: {
			get() {
				return this.center;
			},
			set(newValue) {
				this.$emit('update:center', newValue)
			}
		},
		localZoom: {
			get() {
				return this.zoom;
			},
			set(newVal) {
				this.$emit('update:zoom', newVal);
			}
		},
		localBounds: {
			get() {
				return this.bounds;
			},
			set(newVal) {
				this.$emit('update:bounds', newVal);
			}
		},
		localMaxBounds: {
			get() {
				return this.maxBounds;
			},
			set(newVal) {
				this.$emit('update:maxBounds', newVal);
			}
		},
	},

	methods: {
		setViewTo(coordinates) {
			this.$refs.map.mapObject.setView(coordinates, this.zoom, this.$refs.map.mapObject.options);
		},
	},

	beforeDestroy() {
		this.$refs.map.mapObject.off('click', e => this.$emit('click-map', e.latlng));
		this.$refs.map.mapObject.off('contextmenu', e => this.menu.active(e));
	},

	mounted() {
		this.$refs.map.mapObject.on('click', e => this.$emit('click-map', e.latlng));
		this.$refs.map.mapObject.on('contextmenu', e => this.menu.active(e));

		if (this.disableMoving) {
			this.$refs.map.mapObject.dragging.disable();
			this.$refs.map.mapObject.touchZoom.disable();
			this.$refs.map.mapObject.doubleClickZoom.disable();
			this.$refs.map.mapObject.scrollWheelZoom.disable();
			this.$refs.map.mapObject.boxZoom.disable();
			this.$refs.map.mapObject.keyboard.disable();
			if (this.$refs.map.mapObject.tap) {
				this.$refs.map.mapObject.tap.disable();
			}
		}
	},
	/* eslint-enable vue/order-in-components */
};
</script>

<style lang="scss">
.v-map {
	height: 100%;

	&__wrapper {
		z-index: 0;
	}
	.leaflet-control-zoom {
		border-width: 1px;
		border-radius: 15px;
		overflow: hidden;

		.leaflet-control-zoom-in, .leaflet-control-zoom-out {
			display: flex;
			justify-content: center;
			align-items: center;
			width: 25px;
			height: 25px;
			font-size: 18px;
		}
	}
	.leaflet-control-layers {
		border-width: 1px;
		border-radius: 10px;
		.leaflet-control-layers-toggle {
			width:	34px;
			height:	34px;
		}
	}
	.leaflet-control-attribution {
		display: none;
	}
	
	.leaflet-routing-collapse-btn {
		color: #333;
	}
	.container-routing {
		&.leaflet-routing-container-hide {
			margin-bottom: 0 !important;
			width: 45px;
			height: 45px;
			padding: 2px;
			
			.leaflet-routing-collapse-btn {
				cursor: pointer;
				top: 20%;
			}
		}
	}
	.alternative-panel {
		padding-top: 20px;
	}
}
</style>